/**
 * API Client for FastAPI Backend
 * Centralized HTTP client với authentication và error handling
 */

// Tự động chọn URL dựa trên môi trường
const API_BASE_URL = import.meta.env.DEV
  ? (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000')
  : '/api';

interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: any;
  headers?: Record<string, string>;
  requireAuth?: boolean;
}

interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  detail?: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get JWT token from localStorage
   */
  private getToken(): string | null {
    return localStorage.getItem('jwt_token');
  }

  /**
   * Set JWT token to localStorage
   */
  public setToken(token: string): void {
    localStorage.setItem('jwt_token', token);
  }

  /**
   * Remove JWT token from localStorage
   */
  public clearToken(): void {
    localStorage.removeItem('jwt_token');
  }

  /**
   * Build headers for request
   */
  private buildHeaders(options: ApiRequestOptions): Headers {
    const headers = new Headers();

    // Add default headers
    headers.append('Content-Type', 'application/json');

    // Add authentication if required
    if (options.requireAuth !== false) {
      const token = this.getToken();
      if (token) {
        headers.append('Authorization', `Bearer ${token}`);
      }
    }

    // Add custom headers
    if (options.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        headers.append(key, value);
      });
    }

    return headers;
  }

  /**
   * Make API request
   */
  async request<T = any>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const { method = 'GET', body, requireAuth = true } = options;

    const url = `${this.baseUrl}${endpoint}`;
    const headers = this.buildHeaders({ ...options, requireAuth });

    console.log(`📡 API Request: ${method} ${url}`);


    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      // Parse response
      const data = await response.json().catch(() => ({}));

      // Handle errors
      if (!response.ok) {
        return {
          error: data.detail || data.error || `HTTP ${response.status}`,
          detail: data.detail,
        };
      }

      return { data };
    } catch (error) {
      console.error('API request failed:', error);
      return {
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * GET request
   */
  async get<T = any>(endpoint: string, requireAuth = true): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', requireAuth });
  }

  /**
   * POST request
   */
  async post<T = any>(
    endpoint: string,
    body?: any,
    requireAuth = true
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body, requireAuth });
  }

  /**
   * PUT request
   */
  async put<T = any>(
    endpoint: string,
    body?: any,
    requireAuth = true
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body, requireAuth });
  }

  /**
   * DELETE request
   */
  async delete<T = any>(endpoint: string, requireAuth = true): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE', requireAuth });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export types
export type { ApiResponse, ApiRequestOptions };

